/**
 * The main-bar menus of the trains kiosk, as on the old one (frontend/src/components/touch/bottom-bar/):
 *  - quick search  a frequent destination, one tap;
 *  - new search    from, to, day and time, then "search trains";
 *  - help          the questions travellers ask most, by topic: a tap says the question;
 *  - filter        on the results: direct, high speed… (the backend reads the sentence), earlier, later.
 * The sentences sent are the old kiosk's (menu-strings.ts): the understanding is used to them.
 */
import type { ActionDef, MenuChip, MenuEntry, ScreenContext } from '@platform/packs/contract';
import { readTrip } from './session';

const divider: MenuEntry = { divider: true };

/** Today or tomorrow as YYYY-MM-DD, on the kiosk's own clock (not UTC: never a day off at midnight). */
export function localDate(day: 'today' | 'tomorrow', now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (day === 'tomorrow' ? 1 : 0));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The hour a part of the day asks for; `now` is the current time. */
export function timeOf(part: string, now = new Date()): string {
  const fixed: Record<string, string> = { morning: '08:00', afternoon: '14:00', evening: '18:00' };
  return fixed[part] ?? `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

// ── quick search ─────────────────────────────────────────────────────────────

/** The old kiosk's presets: from the kiosk's station (the backend's default origin) to these. */
export const QUICK_SEARCH = [
  { city: 'Milano', station: 'Milano Centrale', emoji: '🏙️' },
  { city: 'Napoli', station: 'Napoli Centrale', emoji: '🌋' },
  { city: 'Firenze', station: 'Firenze Santa Maria Novella', emoji: '🎨' },
  { city: 'Torino', station: 'Torino Porta Nuova', emoji: '🏔️' },
  { city: 'Venezia', station: 'Venezia Santa Lucia', emoji: '🛶' },
  { city: 'Bologna', station: 'Bologna Centrale', emoji: '🍝' },
  { city: 'Verona', station: 'Verona Porta Nuova', emoji: '🏛️' },
  { city: 'Genova', station: 'Genova Piazza Principe', emoji: '⚓' },
  { city: 'Bari', station: 'Bari Centrale', emoji: '🏖️' },
  { city: 'Salerno', station: 'Salerno', emoji: '🌊' },
  { city: 'Reggio Calabria', station: 'Reggio Calabria Centrale', emoji: '🍊' },
  { city: 'Palermo', station: 'Palermo Centrale', emoji: '🍋' },
  { city: 'Catania', station: 'Catania Centrale', emoji: '🌋' },
] as const;

export const quickSearch: ActionDef = {
  id: 'quick-search',
  label: 'trains.action.quick_search',
  icon: 'sparkles',
  gate: 'search',
  menu: {
    view: () => QUICK_SEARCH.map((q): MenuChip => ({
      id: q.station, label: q.city, emoji: q.emoji,
      step: { press: { button: 'search', data: { destination: q.station, date: localDate('today'), time: timeOf('now') } } },
    })),
  },
};

// ── new search ───────────────────────────────────────────────────────────────

/** The cities the from/to views offer (the old kiosk's eight). */
export const SEARCH_CITIES = [
  { city: 'Roma', station: 'Roma Termini', emoji: '🏛️' },
  { city: 'Milano', station: 'Milano Centrale', emoji: '🏙️' },
  { city: 'Napoli', station: 'Napoli Centrale', emoji: '🌋' },
  { city: 'Firenze', station: 'Firenze Santa Maria Novella', emoji: '🎨' },
  { city: 'Torino', station: 'Torino Porta Nuova', emoji: '🏔️' },
  { city: 'Bologna', station: 'Bologna Centrale', emoji: '🍝' },
  { city: 'Venezia', station: 'Venezia Santa Lucia', emoji: '🛶' },
  { city: 'Genova', station: 'Genova Piazza Principe', emoji: '⚓' },
] as const;

const DAYS = ['today', 'tomorrow'] as const;
const TIMES = [
  { id: 'now', icon: 'clock' },
  { id: 'morning', icon: 'sun' },
  { id: 'afternoon', icon: 'sun' },
  { id: 'evening', icon: 'moon' },
] as const;

const cityOf = (station: string | undefined) => SEARCH_CITIES.find((c) => c.station === station)?.city;

function searchView({ view, values }: { view: string; values: Readonly<Record<string, string>> }, { t }: ScreenContext): MenuEntry[] {
  if (view === 'origin' || view === 'destination') {
    return SEARCH_CITIES.map((c) => ({
      id: c.station, label: c.city, emoji: c.emoji, selected: values[view] === c.station,
      step: { set: { [view]: c.station }, open: 'main' },
    }));
  }
  const day = values.date ?? 'today';
  const time = values.time ?? 'now';
  const ready = Boolean(values.origin && values.destination);
  return [
    { id: 'origin', label: cityOf(values.origin) ?? t('trains.menu.from'), icon: 'train', selected: Boolean(values.origin), step: { open: 'origin' } },
    { id: 'destination', label: cityOf(values.destination) ?? t('trains.menu.to'), icon: 'map', selected: Boolean(values.destination), step: { open: 'destination' } },
    divider,
    ...DAYS.map((d): MenuChip => ({ id: d, label: t(`trains.menu.${d}`), icon: 'calendar', selected: day === d, step: { set: { date: d } } })),
    divider,
    ...TIMES.map((x): MenuChip => ({ id: x.id, label: t(`trains.menu.${x.id}`), icon: x.icon, selected: time === x.id, step: { set: { time: x.id } } })),
    divider,
    {
      id: 'go', label: t('trains.menu.search'), icon: 'search', tone: 'main', disabled: !ready,
      step: { press: { button: 'search', data: { origin: values.origin, destination: values.destination, date: localDate(day === 'tomorrow' ? 'tomorrow' : 'today'), time: timeOf(time) } } },
    },
  ];
}

export const newSearch: ActionDef = {
  id: 'new-search',
  label: 'trains.action.new_search',
  icon: 'search',
  gate: 'search',
  menu: { view: searchView },
};

// ── help: the frequent questions ─────────────────────────────────────────────

/** Topics and their questions (the old kiosk's FAQ_ITEMS); labels and sentences in menu-strings.ts. */
export const FAQ = [
  { id: 'booking', emoji: '🎫', questions: [['change_ticket', '🔄'], ['refund', '💰']] },
  { id: 'travel', emoji: '🚆', questions: [['train_delay', '⏱️'], ['luggage', '🧳'], ['seat_assignment', '💺']] },
  { id: 'station', emoji: '🏢', questions: [['find_platform', '🚂'], ['waiting_room', '🪑'], ['restrooms', '🚻'], ['wifi', '📶'], ['parking', '🅿️'], ['accessibility', '♿'], ['elevator', '🛗']] },
  { id: 'ticket', emoji: '🎟️', questions: [['print_ticket', '🖨️'], ['check_pnr', '🔍']] },
  { id: 'other', emoji: '❓', questions: [['lost_found', '🔑'], ['customer_service', '📞']] },
] as const;

function helpView({ view }: { view: string }, { t }: ScreenContext): MenuEntry[] {
  const topic = FAQ.find((f) => `topic:${f.id}` === view);
  if (topic) {
    return topic.questions.map(([id, emoji]): MenuChip => ({ id, label: t(`trains.faq.${id}`), emoji, step: { say: t(`trains.faq.say.${id}`) } }));
  }
  return [
    // asking for help where you are: the backend answers for this screen
    { id: 'ask', label: t('trains.menu.ask_help'), icon: 'help', step: { say: t('trains.action.help.words') } },
    ...FAQ.map((f): MenuChip => ({ id: f.id, label: t(`trains.faq.topic.${f.id}`), emoji: f.emoji, step: { open: `topic:${f.id}` } })),
  ];
}

export const help: ActionDef = {
  id: 'help',
  label: 'trains.action.help',
  icon: 'help',
  gate: null,
  menu: { view: helpView },
};

// ── filter, on the results ───────────────────────────────────────────────────

/** The commands that change the list; the backend parses the sentence (plugin/result_filter.py). A filter that keeps
 *  some trains (`keeps`, the backend's name for it) is offered only when it would change the list shown — the backend
 *  says which (`narrowing_filters`): no «solo regionali» on Italo's trains (03/11). An order is offered always. */
export const FILTERS = [
  { id: 'direct', emoji: '🎯', keeps: 'direct_only' },
  { id: 'frecce', emoji: '🚄', keeps: 'frecce_only' },
  { id: 'regionali', emoji: '🚃', keeps: 'regionale_only' },
  { id: 'intercity', emoji: '🚆', keeps: 'intercity_only' },
  { id: 'cheap', emoji: '💰', keeps: null },
  { id: 'fast', emoji: '⚡', keeps: null },
] as const;

/** HH:MM of a departure (`2026-10-05T09:10:00` or `09:10`). */
const hhmm = (departure: string | undefined) => departure?.match(/(\d{2}:\d{2})/)?.[1];

/** Two hours before the first train shown (not before midnight), minutes kept. */
export function earlierThan(departure: string): string {
  const [h = 0, m = 0] = departure.split(':').map(Number);
  return `${String(Math.max(0, h - 2)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function filterView(_: unknown, ctx: ScreenContext): MenuEntry[] {
  const { t } = ctx;
  const { results, narrowingFilters } = readTrip(ctx.session);
  const first = hhmm(results[0]?.departure_time);
  const last = hhmm(results.at(-1)?.departure_time);
  const offered = FILTERS.filter((f) => f.keeps === null || narrowingFilters.includes(f.keeps));
  return [
    ...offered.map((f): MenuChip => ({ id: f.id, label: t(`trains.filter.${f.id}`), emoji: f.emoji, step: { press: { button: 'filter', data: { type: t(`trains.filter.say.${f.id}`) } } } })),
    divider,
    {
      id: 'earlier', label: t('trains.filter.earlier'), emoji: '⏪',
      step: first ? { press: { button: 'search', data: { time: earlierThan(first) } } } : { press: { button: 'filter', data: { type: t('trains.filter.say.earlier') } } },
    },
    {
      id: 'later', label: t('trains.filter.later'), emoji: '⏩',
      step: last ? { press: { button: 'search', data: { time: last } } } : { press: { button: 'filter', data: { type: t('trains.filter.say.later') } } },
    },
  ];
}

export const filter: ActionDef = {
  id: 'filter',
  label: 'trains.action.filter',
  icon: 'filter',
  gate: 'filter',
  menu: { view: filterView },
};
